import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Alert } from 'react-bootstrap';
import Nestable from 'react-nestable';

import SharePointFolderBrowser from '../components/psc/lib/SharePointFolderBrowser/SharePointFolderBrowser';
import SaveButton from '../components/psc/lib/SaveButton';
import CancelButton from '../components/psc/lib/CancelButton';
import ErrorMessage from '../components/psc/lib/ErrorMessage';
import Checkbox from '../components/psc/lib/Checkbox';

import { setProject } from '../obj/pscState'

import useAuthentication from '../hooks/useAuthentication';
import useSharePointToken from '../hooks/useSharePointToken';
import useUserManagement from '../hooks/useUserManagement';
import useGncHistory from '../hooks/useGncHistory';
import useProjectList from '../hooks/useProjectList';

const ProjectSharePoint = () => {
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [projectSystems, setProjectSystems] = useState([]);
  const [editingSystem, setEditingSystem] = useState(null);
  const [defaultSharePointFolder, setDefaultSharePointFolder] = useState(null);
  const [isUsingSharePointIntegration, setIsUsingSharePointIntegration] = useState(false);
  const [error, setError] = useState(null);
  const [submitSpinner, setSubmitSpinner] = useState(false);
  const [submitActive, setSubmitActive] = useState(false);
  const [authenticateSpinner, setAuthenticateSpinner] = useState(false);
  const productTreeRef = useRef(null);

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const pscState = useSelector(state => state.pscState.value)
  const project = pscState.project
  const projectURL = '/v0/projects/' + project.id +'/sharepoint_configuration'

  const { currentUser } = useAuthentication();

  const {
    tokenIsValid,
    redirectToAuthUrl,
    encryptionKey,
    loading,
    sharePointApplicationIsValid,
    sharePointApplicationError,
  } = useSharePointToken();
  
  const {
      userHasProjectAccessLevel,
  } = useUserManagement()

  const {
    projectUpdate,
  } = useProjectList()

  useEffect(() => {
    setIsUsingSharePointIntegration(project.is_using_sharepoint_integration)
    
    if (project.default_sharepoint_folder && project.default_sharepoint_folder.id > 0) {
        setDefaultSharePointFolder(project.default_sharepoint_folder)
    }
    
    // Initialize project systems if they exist in the project data
    if (project.project_systems && project.project_systems.length > 0) {
        setProjectSystems(project.project_systems)
    }
  }, [project])
  
  const handleAuthenticate = () => {
    setAuthenticateSpinner(true);
    redirectToAuthUrl();
  };

    const _setProjectSystemSharePointFolder = (node, sharepointFolder) => {
        // I don't understand why I need to do this here??? If not, it's all "this is read-only".
        // But it works in Project.js ???
        let newNode = { ...node }
        newNode.children = node.children.map((item) => _setProjectSystemSharePointFolder(item, sharepointFolder))
        if( node.id === editingSystem.id ) {
            newNode.sharepoint_folder = sharepointFolder
        }
        return newNode
    }
    
    const handleFolderSelect = ({ sharepointFolder }) => {
        if (editingSystem) {
            // Update the specific system's sharepoint settings
            const updated = projectSystems.map((system) => _setProjectSystemSharePointFolder(system, sharepointFolder))
            setProjectSystems(updated)
            setEditingSystem(null);
        } else {
            // Update default folder
            setDefaultSharePointFolder(sharepointFolder);
        }
        setShowFolderBrowser(false);
    };
  
  const handleChangeFolder = (system) => {
    setEditingSystem(system);
    setShowFolderBrowser(true);
  };

  const _clearProjectSystemSharePointFolder = (node, systemId) => {
    let newNode = { ...node };
    newNode.children = node.children.map((item) => _clearProjectSystemSharePointFolder(item, systemId));
    if (node.id === systemId) {
      newNode.sharepoint_folder = null;
    }
    return newNode;
  };

  const handleClearFolder = (system) => {
    const updated = projectSystems.map((s) => _clearProjectSystemSharePointFolder(s, system.id));
    setProjectSystems(updated);
  };

  const handleSubmitError = (error) => {
    setError(error);
    setSubmitSpinner(false);
  };

  const renderProductTreeSystem = ({ item, collapseIcon }) => {
    const pathIsDefined = item.sharepoint_folder && item.sharepoint_folder?.site_name !== "" && item.sharepoint_folder?.file_path !== ""
    const siteName = item.sharepoint_folder?.site_name || 'Not set';
    const folderPath = item.sharepoint_folder?.file_path || 'Not set';
    const locationText = `${siteName} | ${folderPath}`;

    return (
      <div className="product-tree-item">
        <div className="product-tree-name">
          <span className="product-tree-collapse">{collapseIcon}</span>
          <span className="product-tree-name-text">{item.name}</span>
        </div>
        <div className="product-tree-sharepoint">
          <div className="sharepoint-location">
            <span {...(pathIsDefined ? { 'data-tooltip-id': 'gnctooltip', 'data-tooltip-content': locationText } : {})} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 30px)' }}>{locationText}</span>
              <button
                type="button"
                className={`btn btn-xs btn-link sharepoint-clear-btn${pathIsDefined ? '' : ' invisible'}`}
                onClick={() => handleClearFolder(item)}
                style={{ marginLeft: 'auto', padding: '0 4px', color: 'rgb(204, 204, 204)', visibility: pathIsDefined ? 'visible' : 'hidden' }}
              >
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>
          <button 
            type="button" 
            className="btn btn-xs btn-primary"
            onClick={() => handleChangeFolder(item)}
          >
            Change
          </button>
        </div>
      </div>
    );
  };

  const createProductTree = () => {
    return (
      <div className="product-tree-container">
        <Nestable
          threshold="25"
          disableCollapse={false}
          disableDrag={true}
          items={projectSystems}
          renderCollapseIcon={(item) => item.isCollapsed
            ? <i className="fa fa-caret-right" aria-hidden="true"></i>
            : <i className="fa fa-caret-down" aria-hidden="true"></i>
          }
          renderItem={renderProductTreeSystem}
          ref={productTreeRef}
        />
      </div>
    );
  };

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (submitActive || !validateBeforeSubmit()) {
      return null;
    }

    setSubmitActive(true);
    setSubmitSpinner(true);
    
    // Recursively prepare project systems data for submission (including children)
    const prepareSystemData = (system) => ({
      id: system.id,
      name: system.name,
      sharepoint_folder: system.sharepoint_folder,
      children: system.children ? system.children.map(prepareSystemData) : []
    });
    const projectSystemsData = projectSystems.map(prepareSystemData);

    const data = {
      is_using_sharepoint_integration: isUsingSharePointIntegration,
      default_sharepoint_folder: defaultSharePointFolder,
      project_systems: projectSystemsData
    }
    
    fetch(projectURL, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                return response.json()
            }
            throw new Error('Network response was not ok.');
        }).then(json => {
            projectUpdate(json.result.id, json.result)
            dispatch(setProject(json.result))
            navigate('/portfolio/' + project.id + '/dashboard')
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error.message);
        });
    return null
  };

  const validateBeforeSubmit = () => {
    let error = []
    if ( isUsingSharePointIntegration && ! defaultSharePointFolder ) {
        error.push("Default Location is required")
    }
    if ( error.length > 0 ) {
        setError(error)
        return false
    }
    return true
  }

  if (loading) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-content">
                <div>Loading token status...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sharePointApplicationIsValid) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-title">
                <h2>SharePoint integration not yet configured</h2>
              </div>
              <div className="ibox-content">
                <Alert variant="warning">
                  In order to use SharePoint, a CADM Admin first needs to configure the SharePoint Application.
                </Alert>
                <p>
                  Error given when trying to validate the SharePoint Application: {sharePointApplicationError}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!tokenIsValid) {
    return (
      <div className="wrapper wrapper-content">
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox float-e-margins">
              <div className="ibox-title">
                <h2>SharePoint</h2>
              </div>
              <div className="ibox-content">
                <Alert variant="warning">
                  <p><strong>Please Authenticate to begin</strong></p>

                  <p>SharePoint integration has been enabled by your CADM Admin.</p>

                  <p>This area allows you to activate and configure SharePoint integration for
                    this {project?.project_type?.name}.</p>
                </Alert>
                <button 
                  className={`btn btn-mr has-spinner btn-primary${authenticateSpinner ? ' active' : ''}`}
                  role="button"
                  onClick={() => handleAuthenticate() }
                >
                  <span className="psc-spinner"><i className="fa fa-refresh fa-spin fa-fw"></i></span>
                  Authenticate via SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const defaultFolderIsDefined = !!defaultSharePointFolder
  const defaultFolderText =  defaultFolderIsDefined ? `${defaultSharePointFolder.site_name} | ${defaultSharePointFolder.file_path}` : "";
  
  return (
    <div className="wrapper wrapper-content">
      <div className="row">
        <div className="col-lg-12">
          <div className="ibox float-e-margins">
            <div className="ibox-title">
              <h2>SharePoint</h2>
              <div key="newText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                <p>
                  The <strong>GNC Go SharePoint Integration</strong> creates a limited connection between GNC Go and your SharePoint sites, allowing automation of certain tasks.
                </p>
                <ul>
                  <li>
                    Set a <strong>Default Location</strong> as the fallback for all documents.
                  </li>
                  <li>
                    Optionally, you can auto-file documents by <strong>Applicability</strong>, by assigning SharePoint locations to your <strong>Product Tree</strong> branches.
                    When a document is created, GNC Go will upload it to the most specific location defined 
                    (the first level above it in the Product Tree with a location set). 
                    If none is found, the Default Location is used.
                  </li>
                </ul>
              </div>
            </div>
            <div className="ibox-content sharepoint">
              <form className="form-horizontal" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="col-sm-2 control-label">Use SharePoint Integration</label>
                  <div className="col-sm-8" style={{ paddingTop: '7px' }}>
                    <Checkbox
                      isChecked={isUsingSharePointIntegration}
                      onChange={(e) => setIsUsingSharePointIntegration(e.target.checked)}
                    />
                    <span className="help-block m-b-none m-t-none" >
                      <small className="form-info-text" > Changes may take a few minutes to reach all users. </small>
                    </span>
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group" style={!isUsingSharePointIntegration ? { opacity: 0.5 } : {}}>
                  <label className={`col-sm-2 control-label${isUsingSharePointIntegration ? ' required' : ''}`}>Default Location</label>
                  <div className="col-sm-8">
                    <div style={{ display: 'flex' }}>
                      <button 
                        type="button" 
                        className="btn btn-mr btn-primary"
                        style={{
                          whiteSpace: 'nowrap',
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          margin: 0,
                          height: '34px',
                          lineHeight: '0.5'
                        }}
                        onClick={() => setShowFolderBrowser(true)}
                        disabled={!isUsingSharePointIntegration}
                      >
                        Change
                      </button>
                      <input 
                        type="text" 
                        className="form-control" 
                        value={defaultFolderText}
                        data-tooltip-id="gnctooltip"
                        data-tooltip-content={defaultFolderText}
                        disabled 
                        style={{
                          flex: 1,
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          marginLeft: '-1px',
                          height: '34px',
                          padding: '6px 12px'
                        }}
                      />
                    </div>
                    <span className="help-block m-b-none m-t-none" >
                      <small className="form-info-text" > All items will be uploaded to this location, unless otherwise defined by product tree location below. </small>
                    </span>
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group" style={!isUsingSharePointIntegration ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                  <label className="col-sm-2 control-label">Product Tree Locations</label>
                  <div className="col-sm-8">
                    {projectSystems.length > 0 ? (
                      createProductTree()
                    ) : (
                       <div className="alert alert-info">No product tree defined. (You can define a product tree in Information).</div>
                    )}
                  </div>
                </div>

                <div className="hr-line-dashed"></div>

                <div className="form-group">
                  <ErrorMessage
                    error={error}
                  />
                  <div className="col-sm-10 col-sm-offset-2">
                    <CancelButton
                        navigateOverride={() => navigate('/portfolio/'+project.id+'/dashboard')}
                    />
                    <SaveButton
                        buttonVariant='primary'
                        visible={userHasProjectAccessLevel(currentUser,project.id, "CADM_CONTROLLER")}
                        spinnerActive={submitSpinner}
                        label='Save Changes'
                        onClick={handleSubmit}
                        onError={handleSubmitError}
                    />
                  </div>
                </div>
              </form>

              <SharePointFolderBrowser
                show={showFolderBrowser}
                onClose={() => setShowFolderBrowser(false)}
                onSelect={handleFolderSelect}
                encryptionKey={encryptionKey}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSharePoint;
